import type { Translation } from './types';
import { hideArrayProperties } from './types';

export const it: Translation = hideArrayProperties<Translation>({
  meta: {
    title: 'Aletiq \u2014 La tua IA costruisce. Aletiq verifica.',
    description:
      'Il tuo agente IA costruisce il tuo sito web e lo verifica automaticamente con Aletiq. Principio a quattro occhi per le web app generate dall\u2019IA.',
    ogTitle: 'Aletiq \u2014 La tua IA costruisce. Aletiq verifica.',
    ogDescription: 'Principio a quattro occhi per le web app generate dall\u2019IA. Automatico, sicuro, zero sforzo.',
  },
  nav: {
    home: 'Home',
    howItWorks: 'Come funziona',
    useCases: 'Casi d\u2019uso',
    security: 'Sicurezza',
    pricing: 'Prezzi',
    contact: 'Contatto',
    blog: 'Blog',
    cta: 'Inizia gratis',
    languageSwitch: 'IT',
  },

  // ── Hero ────────────────────────────────────────────────────────────────────
  hero: {
    badge: 'Per Claude Code, Cursor e Codex via MCP',
    headlineLine1: 'La tua IA costruisce \u2014',
    headlineLine2: 'Aletiq verifica',
    subheadline:
      'Il tuo agente IA scrive codice pi\u00f9 velocemente di quanto tu possa controllare. Aletiq verifica automaticamente, fornisce feedback all\u2019agente, che corregge i problemi sul momento \u2014 tu vedi solo il risultato finale.',
    ctaPrimary: 'Richiedi accesso anticipato',
    ctaSecondary: 'Come funziona',
    chatFlow: [
      { role: 'user', text: 'Costruiscimi un negozio online con checkout' },
      { role: 'agent', text: 'Costruisco pagina prodotto, carrello e checkout...' },
      { role: 'agent', text: 'aletiq --verify "testa il checkout" --page /checkout' },
      { role: 'aletiq', text: 'Dati di test preparati: prodotto + indirizzo inseriti', status: 'pass' },
      { role: 'aletiq', text: 'Il pulsante checkout non risponde su mobile', status: 'fail' },
      { role: 'agent', text: 'Bug rilevato \u2014 correggo il touch target...' },
      { role: 'aletiq', text: 'Ri-esecuzione: checkout completato con successo', status: 'pass' },
    ],
    demoLines: [
      { type: 'user', text: 'Costruiscimi un negozio online con checkout' },
      { type: 'blank', text: '' },
      { type: 'agent', text: 'Costruisco pagina prodotto, carrello e checkout.' },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "testa il checkout" --page /checkout' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Dati di test preparati: prodotto + indirizzo inseriti' },
      { type: 'pass', text: 'Navigazione: tutti i link funzionano' },
      { type: 'fail', text: 'Il pulsante checkout non risponde su mobile' },
      { type: 'blank', text: '' },
      { type: 'agent', text: 'Bug rilevato \u2014 correggo il touch target...' },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "re-run" (gratis)' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Ri-esecuzione: checkout completato con successo' },
      { type: 'pass', text: '3/3 verifiche superate' },
    ],
  },

  // ── Works With ──────────────────────────────────────────────────────────────
  worksWith: {
    subtitle: 'Integrazione',
    heading: 'Il meglio con',
    headingHighlight: 'Claude Code',
    description: 'Aletiq usa MCP \u2014 lo standard aperto per l\u2019integrazione degli strumenti. Claude Code ha degli hook che chiamano Aletiq automaticamente dopo ogni modifica. Cursor e Codex funzionano tramite rules.',
    agents: [
      { name: 'Claude Code', logo: '/images/agents/claude-code.svg' },
      { name: 'Cursor', logo: '/images/agents/cursor.svg' },
      { name: 'Codex', logo: '/images/agents/codex.svg' },
      { name: 'Windsurf', logo: '/images/agents/windsurf.svg' },
      { name: 'Cline', logo: '/images/agents/cline.svg' },
      { name: 'Continue', logo: '/images/agents/continue.svg' },
    ],
  },

  // ── How It Works (Homepage short version) ───────────────────────────────────
  howItWorks: {
    label: 'Come funziona',
    heading: 'Come',
    headingHighlight: 'funziona',
    intro: 'Descrivi ci\u00f2 che vuoi. Il tuo agente IA lo costruisce, lo verifica con Aletiq \u2014 e tu vedi il risultato.',
    steps: [
      {
        number: '01',
        title: 'Tu descrivi',
        description: 'Di\u2019 al tuo agente IA ci\u00f2 di cui hai bisogno. Con parole tue, come in una conversazione.',
      },
      {
        number: '02',
        title: 'Il tuo agente costruisce e verifica',
        description: 'L\u2019agente costruisce la tua funzionalit\u00e0, chiama Aletiq, riceve feedback \u2014 e corregge i problemi sul momento.',
      },
      {
        number: '03',
        title: 'Tu vedi il risultato',
        description: 'Aletiq restituisce un verdetto chiaro al tuo agente: superato, attenzione richiesta, o fallito \u2014 direttamente nel tuo flusso di lavoro.',
      },
    ],
    linkText: 'Scopri di pi\u00f9',
    linkButton: 'Vedi i dettagli',
  },

  // ── Why Aletiq (replaces Verification Levels) ──────────────────────────────
  whyAletiq: {
    label: 'Perch\u00e9 Aletiq?',
    heading: 'Perch\u00e9 i team',
    headingHighlight: 'scelgono Aletiq',
    cards: [
      {
        icon: '\u{1F4BB}',
        title: 'Gira sulla tua macchina',
        description: 'Il runner Aletiq testa direttamente su localhost \u2014 zero latenza, nessun deploy necessario. Il tuo agente riceve feedback in secondi, non in minuti.',
      },
      {
        icon: '\u{1F504}',
        title: 'Ciclo di feedback chiuso',
        description: 'Aletiq trova un bug, il tuo agente lo corregge immediatamente \u2014 senza che tu debba fare nulla. Anche i dati di test vengono preparati automaticamente.',
      },
      {
        icon: '\u{1F512}',
        title: 'I tuoi dati restano locali',
        description: 'Nessun server cloud vede il tuo codice. Il runner gira sulla tua macchina \u2014 questo rende Aletiq economico per noi e sicuro per te.',
      },
      {
        icon: '\u{1F3AF}',
        title: 'Verdetti chiari',
        description: 'Il tuo agente chiama aletiq --verify e riceve un risultato univoco: superato o fallito \u2014 con dettagli concreti per la correzione.',
      },
    ],
  },

  // ── Use Cases (Homepage cards) ──────────────────────────────────────────────
  useCases: {
    label: 'Casi d\u2019uso',
    heading: 'Cosa Aletiq',
    headingHighlight: 'verifica per te',
    description: 'Qualunque cosa il tuo agente IA costruisca \u2014 Aletiq verifica che funzioni.',
    viewAllButton: 'Vedi tutti gli scenari',
    cards: [
      {
        slug: 'online-shop',
        icon: '\u{1F6D2}',
        title: 'Il mio checkout funziona?',
        description: 'Il tuo agente ha costruito un negozio online. Aletiq verifica che i clienti possano andare dal carrello all\u2019ordine completato.',
      },
      {
        slug: 'responsive',
        icon: '\u{1F4F1}',
        title: 'Si vede bene su mobile?',
        description: 'Aletiq apre il tuo sito su diverse dimensioni di schermo e verifica che tutto venga visualizzato correttamente.',
      },
      {
        slug: 'contact-form',
        icon: '\u2709\uFE0F',
        title: 'Il modulo di contatto funziona?',
        description: 'Aletiq compila il tuo modulo, lo invia e verifica che il messaggio arrivi.',
      },
      {
        slug: 'navigation',
        icon: '\u{1F9ED}',
        title: 'La navigazione funziona?',
        description: 'Aletiq naviga nel tuo menu e verifica che tutti i link portino alla destinazione corretta.',
      },
    ],
  },

  // ── CTA ─────────────────────────────────────────────────────────────────────
  cta: {
    headline: 'Pronto?',
    headlineHighlight: 'Inizia gratis',
    button: 'Inizia gratis \u2192',
    card: {
      title: 'Inizia gratis',
      description: '50 verifiche al mese. Ri-esecuzioni illimitate. Nessuna carta di credito richiesta.',
      link: 'Vedi tutti i piani \u2192',
    },
  },

  // ── Proof Bar ───────────────────────────────────────────────────────────────
  proofBar: {
    badges: [
      { icon: '\u{1F501}', text: 'Ri-esecuzioni sempre gratis' },
      { icon: '\u{1F9E0}', text: 'Effetto memoria muscolare' },
      { icon: '\u{1F916}', text: 'Zero sforzo manuale' },
      { icon: '\u{1F4B0}', text: 'Fino al 91% pi\u00f9 economico' },
    ],
  },

  // ── Muscle Memory ──────────────────────────────────────────────────────────
  muscleMemory: {
    label: 'Memoria muscolare',
    heading: 'Testa una volta,',
    headingHighlight: 'protetto per sempre',
    description:
      'Aletiq ricorda ogni test. Ogni nuova build viene automaticamente verificata contro tutti i test precedenti \u2014 gratis. La tua rete di sicurezza cresce con ogni funzionalit\u00e0, senza che tu debba fare nulla.',
    steps: [
      { number: 'Settimana 1', label: 'Modulo di contatto costruito', checks: '4 test' },
      { number: 'Settimana 2', label: '+ Checkout aggiunto', checks: '11 test' },
      { number: 'Settimana 4', label: '+ Blog e Dashboard', checks: '31 test' },
      { number: 'Settimana 8', label: '+ Pagamento e Login', checks: '47 ri-esecuzioni gratis' },
    ],
    caption: 'I nuovi test costano token. Tutte le 47 ri-esecuzioni: $0.',
  },

  // ── Token Savings ──────────────────────────────────────────────────────────
  tokenSavings: {
    label: 'Costi ed efficienza',
    heading: '',
    headingHighlight: '91% pi\u00f9 economico dei test manuali',
    description:
      'Il tuo agente IA scrive codice pi\u00f9 velocemente di quanto tu possa controllare. Senza Aletiq, testi tutto manualmente \u2014 oppure trovi i bug in produzione. Con Aletiq: automatico, istantaneo, fino al 91% pi\u00f9 economico.',
    comparison: [
      { label: 'Test manuali', tokens: 'Controlli ogni build tu stesso', bar: 30 },
      { label: 'Nessuna verifica', tokens: 'Bug in produzione \u2192 correzione costosa', bar: 15 },
      { label: 'Con Aletiq', tokens: 'Automatico, ri-esecuzioni gratis', bar: 100, highlight: true },
    ],
    caption: 'Gira localmente sulla tua macchina + strategia modello misto (ri-esecuzioni con modello veloce) = fino al 91% pi\u00f9 economico.',
  },

  // ── Sticky CTA ─────────────────────────────────────────────────────────────
  stickyCta: {
    text: 'Ri-esecuzioni gratis',
    button: 'Inizia gratis',
  },

  // ── Pricing ─────────────────────────────────────────────────────────────────
  pricing: {
    label: 'Prezzi',
    title: 'Semplice. Trasparente. Giusto.',
    popularBadge: 'Pi\u00f9 popolare',
    allPlansLabel: 'Incluso in tutti i piani',
    tiers: [
      {
        name: 'Free',
        price: '$0',
        period: 'per sempre',
        verifications: '100 verifiche/mese',
        features: [
          'Verifiche base (link, moduli, layout)',
          'Ri-esecuzioni illimitate',
          'Un progetto',
          'Supporto community',
        ],
        cta: 'Inizia gratis',
      },
      {
        name: 'Builder',
        price: '$39',
        period: '/mese',
        verifications: '500 verifiche/mese',
        features: [
          'Verifiche estese (+ funzionalit\u00e0)',
          'Ri-esecuzioni illimitate',
          'Fino a 3 progetti',
          'Supporto via email',
          'Eccedenza $0,03/verifica',
        ],
        cta: 'Inizia con Builder',
      },
      {
        name: 'Production',
        price: '$89',
        period: '/mese',
        verifications: '3.000 verifiche/mese',
        features: [
          'Verifiche complete (+ flussi di lavoro)',
          'Ri-esecuzioni illimitate',
          'Fino a 10 progetti',
          'Accesso CI e API',
          'Coda prioritaria',
          'Eccedenza $0,05/verifica',
        ],
        cta: 'Inizia Production',
        popular: true,
      },
      {
        name: 'Enterprise',
        price: 'Personalizzato',
        period: 'su richiesta',
        verifications: 'Quota personalizzata',
        features: [
          'Progetti illimitati',
          'SSO / SAML',
          'Spazio di lavoro team',
          'Merge gating',
          'Regole di verifica personalizzate',
          'Supporto dedicato + SLA',
        ],
        cta: 'Contatta le vendite',
        contactSales: true,
      },
    ],
    allPlansInclude: [
      'Ri-esecuzioni illimitate e gratuite',
      'Runner locale sulla tua macchina',
      'Integrazione MCP (Claude Code, Cursor, Codex)',
      'Preparazione automatica dei dati di test',
    ],
    faq: {
      label: 'FAQ',
      title: 'Domande frequenti',
      items: [
        {
          question: 'Le ri-esecuzioni sono davvero gratuite?',
          answer: 'S\u00ec. Ogni ri-esecuzione di un test esistente \u00e8 completamente gratuita \u2014 indipendentemente da quante volte. Solo le nuove verifiche consumano la tua quota.',
        },
        {
          question: 'Cosa conta come una verifica?',
          answer: 'Ogni chiamata aletiq --verify con un nuovo caso di test conta come una verifica. Le ri-esecuzioni dei test esistenti sono gratuite.',
        },
        {
          question: 'Posso fare upgrade o downgrade in qualsiasi momento?',
          answer: 'S\u00ec. Puoi cambiare piano in qualsiasi momento. La fatturazione viene adattata in modo proporzionale.',
        },
        {
          question: 'Cosa succede quando raggiungo il mio limite?',
          answer: 'Le ri-esecuzioni continuano a funzionare senza limiti. Le nuove verifiche vengono messe in coda fino al prossimo ciclo di fatturazione.',
        },
        {
          question: 'Quali agenti IA funzionano con Aletiq?',
          answer: 'Claude Code (via hook \u2014 integrazione deterministica), Cursor (via rules), Codex (via AGENTS.md) e qualsiasi agente compatibile con MCP.',
        },
        {
          question: 'Serve una carta di credito per il piano Free?',
          answer: 'No. Il piano Free \u00e8 gratuito per sempre, senza carta di credito. 100 verifiche al mese, ri-esecuzioni illimitate.',
        },
      ],
    },
  },

  // ── How It Works (detail page) ──────────────────────────────────────────────
  howItWorksPage: {
    title: 'Come funziona Aletiq',
    subtitle: 'Il tuo agente IA costruisce. Aletiq verifica automaticamente. Tu vedi il risultato.',
    heroLabel: 'Come funziona',
    heroTitle: 'Il tuo agente costruisce \u2014',
    heroHighlight: 'Aletiq verifica',
    steps: [
      {
        number: '01',
        title: 'Descrivi ci\u00f2 che vuoi',
        description: 'Parli con il tuo agente IA \u2014 non direttamente con Aletiq. Aletiq lavora in background, invisibile per te.',
        details: [
          '"Costruiscimi un negozio online con carrello"',
          '"Aggiungi un modulo di contatto"',
          '"Crea una landing page per la mia startup"',
        ],
      },
      {
        number: '02',
        title: 'Il tuo agente costruisce \u2014 e verifica automaticamente',
        description: 'L\u2019agente ha Aletiq integrato come strumento. Dopo ogni funzionalit\u00e0, chiama aletiq --verify. Aletiq testa, prepara i dati di test e fornisce feedback \u2014 l\u2019agente corregge i problemi sul momento.',
        details: [
          'Ciclo chiuso: verifica \u2192 feedback \u2192 correzione \u2192 ri-verifica',
          'Dati di test preparati automaticamente (moduli, login, ordini)',
          'Nessun intervento necessario \u2014 agente e Aletiq gestiscono tutto insieme',
        ],
      },
      {
        number: '03',
        title: 'Vedi risultati chiari',
        description: 'Superato, attenzione richiesta o fallito \u2014 con dettagli concreti su ci\u00f2 che \u00e8 stato verificato. Il tuo agente pu\u00f2 reagire immediatamente.',
        details: [
          'Superato: tutto funziona come previsto',
          'Attenzione: qualcosa sembra sospetto (es. caricamento lento)',
          'Fallito: qualcosa non funziona \u2014 Aletiq dice al tuo agente esattamente cosa',
        ],
      },
    ],
    resultLabels: {
      pass: 'Superato',
      warn: 'Attenzione',
      fail: 'Fallito',
    },
  },

  // ── Use Cases (detail page) ─────────────────────────────────────────────────
  useCasesPage: {
    title: 'Cosa Aletiq verifica per te',
    subtitle: 'Qualunque cosa il tuo agente IA costruisca \u2014 Aletiq verifica che funzioni.',
    heroLabel: 'Casi d\u2019uso',
    heroTitle: 'Qualunque cosa il tuo agente costruisca \u2014',
    heroHighlight: 'Aletiq la verifica',
    terminalLabel: 'Esempio dal vivo',
    terminalTitle: 'Ecco come appare una verifica',
    terminalDescription: 'Il tuo agente chiama aletiq --verify. Aletiq prepara i dati di test, verifica tutto e fornisce feedback concreto.',
    scenariosLabel: 'Scenari',
    scenariosTitle: 'Ci\u00f2 che il tuo agente costruisce \u2014',
    scenariosHighlight: 'Aletiq lo verifica',
    cards: [
      {
        slug: 'online-shop',
        icon: '\u{1F6D2}',
        title: 'Negozio online',
        whatYouSay: 'Costruiscimi un negozio con carrello',
        whatAletiqChecks: 'I clienti possono aggiungere prodotti e completare il checkout?',
      },
      {
        slug: 'landing-page',
        icon: '\u{1F680}',
        title: 'Landing Page',
        whatYouSay: 'Crea una landing page per la mia startup',
        whatAletiqChecks: 'Tutti i link funzionano? Si vede bene su mobile?',
      },
      {
        slug: 'sistema-prenotazioni',
        icon: '\u{1F4C5}',
        title: 'Sistema di prenotazione',
        whatYouSay: 'Ho bisogno di uno strumento per prenotare appuntamenti',
        whatAletiqChecks: 'Gli utenti possono selezionare e confermare gli appuntamenti?',
      },
      {
        slug: 'dashboard',
        icon: '\u{1F4CA}',
        title: 'Dashboard',
        whatYouSay: 'Costruiscimi una dashboard clienti',
        whatAletiqChecks: 'I dati si caricano? La navigazione funziona?',
      },
      {
        slug: 'blog',
        icon: '\u{1F4DD}',
        title: 'Blog',
        whatYouSay: 'Crea un blog con commenti',
        whatAletiqChecks: 'I visitatori possono leggere i post e lasciare commenti?',
      },
      {
        slug: 'area-riservata',
        icon: '\u{1F510}',
        title: 'Area riservata',
        whatYouSay: 'Costruisci un\u2019area login per i miei clienti',
        whatAletiqChecks: 'Registrazione, login e recupero password funzionano?',
      },
    ],
  },

  // ── Security Page (replaces Architecture) ───────────────────────────────────
  securityPage: {
    title: 'Sicurezza e privacy',
    subtitle: 'I tuoi dati restano con te. Sempre.',
    heroLabel: 'Sicurezza',
    heroTitle: 'I tuoi dati restano',
    heroHighlight: 'con te. Sempre.',
    architectureLabel: 'Architettura',
    architectureTitle: 'Tutto gira sulla tua macchina',
    architectureDescription: 'Il runner Aletiq gira localmente sulla tua macchina. Il tuo codice, i tuoi dati e i tuoi test non lasciano mai la tua rete. Solo statistiche di utilizzo anonimizzate raggiungono i nostri server.',
    points: [
      {
        icon: '\u{1F3E0}',
        title: 'I tuoi dati restano con te',
        description: 'Tutti i test girano sulla tua macchina. Il tuo codice e i tuoi dati non lasciano mai la tua rete.',
      },
      {
        icon: '\u{1F441}\uFE0F',
        title: 'Non vediamo il tuo codice',
        description: 'Solo i risultati dei test e le osservazioni vengono analizzati. Il tuo codice sorgente resta privato.',
      },
      {
        icon: '\u{1F916}',
        title: 'Il tuo agente, il tuo controllo',
        description: 'Aletiq fa solo ci\u00f2 che il tuo agente chiede. Nessuna azione autonoma, nessun accesso nascosto.',
      },
      {
        icon: '\u{1F512}',
        title: 'Comunicazione crittografata',
        description: 'Tutte le connessioni sono crittografate. I tuoi dati sono al sicuro in transito come a riposo.',
      },
    ],
  },

  // ── Contact ─────────────────────────────────────────────────────────────────
  contact: {
    title: 'Contatto',
    subtitle: 'Scrivici \u2014 ti rispondiamo rapidamente',
    nameLabel: 'Nome',
    emailLabel: 'Email',
    companyLabel: 'Azienda',
    messageLabel: 'Messaggio',
    submitButton: 'Invia messaggio',
    salesHeading: 'Domande su Premium o Team?',
    validation: {
      nameMin: 'Il nome deve avere almeno 2 caratteri',
      emailInvalid: 'Inserisci un indirizzo email valido',
      messageMin: 'Il messaggio deve avere almeno 10 caratteri',
    },
    status: {
      success: 'Il tuo messaggio \u00e8 stato inviato. Ti rispondiamo a breve!',
      error: 'Qualcosa \u00e8 andato storto. Riprova pi\u00f9 tardi.',
    },
  },
  earlyAccess: {
    title: 'Richiedi accesso anticipato',
    subtitle: 'Aletiq è in accesso anticipato. Onboardiamo i partner di design manualmente per assicurarci che il prodotto si adatti al tuo flusso di lavoro.',
    badge: 'Accesso anticipato',
    intro: 'Raccontaci cosa stai costruendo e come lo verifichi oggi. Se c’è un fit, ti contatteremo entro 2 giorni lavorativi con una configurazione privata.',
    trustBullets: [
      { icon: '•', text: 'Nessuna carta di credito richiesta' },
      { icon: '•', text: 'Posti limitati — stiamo onboardando partner di design' },
      { icon: '•', text: 'Risposta entro 2 giorni lavorativi' },
    ],
    form: {
      nameLabel: 'Il tuo nome',
      emailLabel: 'Email di lavoro',
      emailHint: 'Usa un’email di lavoro — ci aiuta a dare priorità.',
      companyLabel: 'Azienda',
      stackLabel: 'Stack / framework',
      stackPlaceholder: 'es. Next.js + Vercel, SvelteKit, Rails',
      goalLabel: 'Cosa vuoi verificare?',
      goalPlaceholder: 'Brevemente: quali flussi costruisce il tuo agente IA e dove si rompe attualmente? (1–2 frasi)',
      githubLabel: 'Handle GitHub (opzionale)',
      githubHint: 'Opzionale — ci aiuta a capire meglio il tuo lavoro.',
      submitButton: 'Richiedi accesso',
      submittingButton: 'Invio in corso…',
    },
    validation: {
      nameMin: 'Inserisci il tuo nome',
      emailInvalid: 'Inserisci un’email di lavoro valida',
      stackMin: 'Indica il tuo stack',
      goalMin: 'Descrivi cosa vuoi verificare (almeno 10 caratteri)',
    },
    status: {
      successTitle: 'Ricevuto — grazie!',
      successDetail: 'Esamineremo la tua richiesta e ti risponderemo entro 2 giorni lavorativi. Controlla la tua casella (anche lo spam) per la conferma.',
      error: 'Qualcosa è andato storto. Riprova o scrivici a hello@aletiq.ai.',
    },
  },
  thankYou: {
    title: 'Grazie — messaggio ricevuto.',
    subtitle: 'Ti risponderemo entro 1–2 giorni lavorativi. Controlla anche la cartella spam.',
    backHome: 'Torna alla Home',
  },
  notFound: {
    title: 'Pagina non trovata',
    description: 'La pagina che stai cercando non esiste o \u00e8 stata spostata.',
    backHome: 'Torna alla Home',
  },
  footer: {
    copyright: '\u00a9 2026 Aletiq. Tutti i diritti riservati.',
    builtBy: 'Realizzato da Denqfabrik',
    privacy: 'Privacy Policy',
    terms: 'Termini di servizio',
    imprint: 'Note legali',
  },
  structuredData: {
    description:
      'Aletiq verifica automaticamente che le web app generate dall\u2019IA funzionino correttamente. Principio a quattro occhi per gli agenti IA \u2014 sicuro, veloce, zero sforzo.',
    offerDescriptions: [
      'Controllo qualit\u00e0 automatico per siti web generati dall\u2019IA',
      'Principio a quattro occhi: il tuo agente costruisce, Aletiq verifica',
      'Test reali nel browser con verdetti chiari per il tuo agente',
    ],
  },
});
